<template>
  <div>
    <div class="flex justify-between items-start gap-4">
      <div class="lg:w-full">
        <div class="flex justify-between items-center">
          <header class="font-bold">Visible to Agent</header>
          <router-link :to="{ name: 'PipelineReviewSettings', params: { tab: 'questions' } }">
            <Button variant="card-header-action" size="sm">Manage questions</Button>
          </router-link>
        </div>
        <p class="text-sm text-gray-500 mb-8">This will be sent to the agent after you save it.</p>

        <HFetchQuery :query="questionsQuery">
          <template #default="{ data }">
            <QuillInput
              v-for="question in data"
              :key="question.id"
              :model-value="answers[question.id] ?? ''"
              :label="question.question"
              :name="`question_${question.id}`"
              @update:model-value="(value: string) => (answers[question.id] = value)"
            />
          </template>
        </HFetchQuery>

        <hr class="my-8 -ml-6 -mr-6" />

        <header class="font-bold">Private</header>
        <p class="text-sm text-gray-500 mb-8">This is not visible to the agent.</p>

        <QuillInput v-model="privateNotes" label="Your Private Notes" name="private_notes" />

        <label class="mt-4 block text-sm leading-5 font-medium text-gray-700 mb-1">Pipeline Performance</label>
        <ScorePicker :score="score" @set-score="s => (score = s)" />

        <Button class="mt-8" :disabled="!hasAnswerContent" @click="submitReview">Save pipeline review</Button>
      </div>
    </div>

    <Dialog :open="showLeaveConfirm" @update:open="showLeaveConfirm = $event">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogDescription>
            You have unsaved changes that will be lost. Are you sure you want to leave?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button @click="confirmLeave">Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';
import ScorePicker from './ScorePicker.vue';
import QuillInput from '@/components/shared/QuillInput.vue';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import { Api } from '@/api';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const props = defineProps<{
  teammate: { id: number };
  contactEvent?: { id: number };
  files?: File[];
}>();

const emit = defineEmits<{
  created: [reviewId: number];
}>();

const router = useRouter();
const loadingSpinnerStore = useLoadingSpinnerStore();

const questionsQuery = useHFetchQuery(() => Api.PipelineReviewQuestions.listQuestions());
const { data: questions } = questionsQuery;

const answers = ref<Record<number, string>>({});
const privateNotes = ref('');
const score = ref<number | null>(null);
const showLeaveConfirm = ref(false);
const pendingRoute = ref<RouteLocationNormalized | null>(null);
const allowLeave = ref(false);

const hasText = (value: string) => value.replace(/<[^>]+>/g, '').trim().length > 0;

const hasAnswerContent = computed(() => Object.values(answers.value).some(hasText));
const hasFormContent = computed(() => hasAnswerContent.value || hasText(privateNotes.value) || score.value !== null);

const confirmLeave = () => {
  showLeaveConfirm.value = false;
  allowLeave.value = true;
  if (pendingRoute.value) router.push(pendingRoute.value.fullPath);
};

const submitReview = async () => {
  loadingSpinnerStore.showLocalLoadingSpinner();

  const response = await Api.PipelineReviews.createPipelineReview(props.teammate.id, {
    private_notes: privateNotes.value,
    score: score.value,
    contact_event_id: props.contactEvent?.id,
    files: props.files,
    answers: (questions.value ?? [])
      .map(q => ({ question_id: q.id, answer: answers.value[q.id] ?? '' }))
      .filter(a => hasText(a.answer)),
  });

  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (response.status === 'success') {
    allowLeave.value = true;
    toast.success('Pipeline Review Created', {
      description: 'Your pipeline review was successfully created.',
    });
    emit('created', response.data.id);
  } else {
    toast.error('Pipeline Review Error', {
      description: response.messages.join(', '),
    });
  }
};

onBeforeRouteLeave(to => {
  if (allowLeave.value || !hasFormContent.value) return true;
  pendingRoute.value = to;
  showLeaveConfirm.value = true;
  return false;
});
</script>
